/**
 * useInstalledModulesMenuQuery — obszary zainstalowanych modułów (GET /api/my/installed-modules).
 *
 * Wspólne zapytanie sekcji obszarów (fallback), renderera własnego układu (element „obszar
 * zainstalowany") oraz `useActiveMenuItem` (przypisania widoku pozycji menu modułu). Wydzielone
 * z `Sidebar.tsx`, żeby hooki stron mogły je czytać bez cyklicznego importu paska bocznego.
 * Ten sam klucz cache — jedno żądanie na wszystkich konsumentów.
 *
 * W cache leży CAŁA odpowiedź endpointu (obszary + obowiązujący typ wniosku o nieobecność);
 * konsumenci biorą swój kawałek przez `select`, więc wszystkie zapytania o ten klucz muszą
 * używać tej samej `queryFn` (inaczej kształt danych w cache się rozjedzie).
 */

import type { HrAbsenceDocumentTypeRef } from '@saas/shared';
import { useQuery } from '@tanstack/react-query';

import type { InstalledMenuArea } from '@/components/Sidebar';
import { api } from '@/lib/api';
import { INSTALLED_MODULES_QUERY_KEY } from '@/lib/hr/query-keys';

/**
 * Odpowiedź `/my/installed-modules` — obszary + obowiązujący typ wniosku o nieobecność (skrót HR;
 * `null` = nie wiadomo).
 */
interface InstalledModulesResponse {
  areas: InstalledMenuArea[];
  hrAbsenceDocumentType: HrAbsenceDocumentTypeRef | null;
}

async function fetchInstalledModules(): Promise<InstalledModulesResponse> {
  const res = await api.get<InstalledModulesResponse>('/my/installed-modules');
  return res.data;
}

export function useInstalledModulesMenuQuery(opts?: { enabled?: boolean }) {
  return useQuery({
    queryKey: INSTALLED_MODULES_QUERY_KEY,
    queryFn: fetchInstalledModules,
    select: (data) => data.areas,
    staleTime: 60 * 1000, // 60s — spójne z cache modułów po stronie API
    enabled: opts?.enabled ?? true,
  });
}

/**
 * Obowiązujący typ „wniosek o nieobecność" (skrót „Nieobecności" w HrQuickNav) z tego samego
 * endpointu co obszary — współdzielony queryKey → jeden fetch, dwa selektory. `null` = nie wiadomo.
 */
export function useHrAbsenceDocumentTypeQuery() {
  return useQuery({
    queryKey: INSTALLED_MODULES_QUERY_KEY,
    queryFn: fetchInstalledModules,
    select: (data) => data.hrAbsenceDocumentType,
    staleTime: 60 * 1000,
  });
}

/**
 * Dane „Mapy obszarów" (GET /api/my/area-map): PEŁNY katalog modułów po obszarach, niezależny od
 * menu. Moduły, których klient nie ma, mają pozycje z `inactive: true` (mapa: „Niewykupiony").
 */
export function useAreaMapQuery(opts?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['my', 'area-map'],
    queryFn: async () => {
      const res = await api.get<{ areas: InstalledMenuArea[] }>('/my/area-map');
      return res.data.areas;
    },
    staleTime: 60 * 1000,
    enabled: opts?.enabled ?? true,
  });
}
